import React, {useCallback, useEffect, useRef, useState} from 'react';
import clsx from 'clsx';
import {useSortable} from '@dnd-kit/sortable';
import {CSS} from '@dnd-kit/utilities';
import {faEquals as EqualsIcon} from '@fortawesome/pro-solid-svg-icons';
import {createStyles, withStyles, type WithStyles} from '@mui/styles';
import IconButton from '@netx/core-components/IconButton';
import type {Theme} from '@netx/core-components/Theme';
import Title from '@netx/core-components/Title';
import {getScrollparent} from '@netx/core-next/utils/element';
import {AttributeSet, AttributeSetProps} from '../Set';

const styles = (theme: Theme) =>
	createStyles({
		root: {
			background: theme.palette.background.paper,
			border: `1px solid ${theme.palette.custom.border}`,
			borderTop: 0,
			borderRadius: 4,
			position: 'relative',
			'&$stuck': {
				'& $title': {
					borderBottom: `1px solid ${theme.palette.custom.border}`,
				},
			},
		},
		content: {
			padding: theme.spacing(1, 3, 2, 3),
			display: 'flex',
			flexDirection: 'column',
		},
		stickySentinel: {
			position: 'absolute',
			left: 0,
			right: 0,
			visibility: 'hidden',
		},
		stickySentinel_top: {
			height: 65,
			top: -65,
		},
		stickySentinel_bottom: {
			height: 65,
			bottom: 0,
		},
		title: {
			...theme.mixins.positionSticky,
			borderTop: `1px solid ${theme.palette.custom.border}`,
			display: 'flex',
			alignItems: 'center',
			top: 0,
			background: theme.palette.background.paper,
			padding: theme.spacing(1),
			zIndex: 9,
		},
		isDragging: {
			background: theme.palette.action.hover,
			opacity: 0.5,
			cursor: 'grabbing',
			'& $content, & $title': {
				visibility: 'hidden',
			},
			'& $dragHandle': {
				cursor: 'grabbing',
			},
		},
		dragHandle: {
			marginRight: 8,
			cursor: 'grab',
		},
		titleText: {},
		stuck: {},
		searchable: {
			'&$content': {
				padding: theme.spacing(1, 2, 1.5, 1),
			},
		},
	});

export interface SortableAttributeSetComponentProps extends Omit<
	AttributeSetProps,
	'name'
> {
	disableSort?: boolean;
	title?: string;
}

export const SortableAttributeSetComponent = ({
	classes,
	disableSort,
	title,
	...props
}: WithStyles<typeof styles> & SortableAttributeSetComponentProps) => {
	const sortable = useSortable({id: props.id});
	const ref = useRef<HTMLDivElement>(null);
	const stickyTargetRef = useRef<HTMLDivElement>(null);
	const sentinelTopRef = useRef<HTMLDivElement>(null);
	const sentinelBottomRef = useRef<HTMLDivElement>(null);
	const [stuck, setStuck] = useState(false);

	const style = {
		transform: CSS.Translate.toString(sortable.transform),
		transition: sortable.transition || undefined,
	};

	const bottomStuckRef = useRef(false);
	const topStuckRef = useRef(false);
	const stickyChange = useCallback((stuck: any, target: any) => {
		if (target === 'bottom') bottomStuckRef.current = stuck;
		if (target === 'top') topStuckRef.current = stuck;
		setStuck(bottomStuckRef.current && topStuckRef.current);
	}, []);

	useEffect(() => {
		const scrollContainer = getScrollparent(ref.current as HTMLDivElement);
		if (
			!scrollContainer ||
			!sentinelBottomRef.current ||
			!sentinelTopRef.current
		)
			return;
		const footerObserver = new IntersectionObserver(
			([record]) => {
				const targetInfo = record.boundingClientRect;
				const rootBoundsInfo = record.rootBounds;
				const ratio = record.intersectionRatio;
				if (!rootBoundsInfo) return;

				if (targetInfo.bottom > rootBoundsInfo.top && ratio === 1) {
					stickyChange(true, 'bottom');
				} else if (
					targetInfo.top < rootBoundsInfo.top &&
					targetInfo.bottom < rootBoundsInfo.bottom
				) {
					stickyChange(false, 'bottom');
				}
			},
			{threshold: [1], root: scrollContainer},
		);
		footerObserver.observe(sentinelBottomRef.current as Element);

		const headerObserver = new IntersectionObserver(
			([record]) => {
				const targetInfo = record.boundingClientRect;
				const rootBoundsInfo = record.rootBounds;
				if (!rootBoundsInfo) return;

				if (targetInfo.bottom < rootBoundsInfo.top) {
					stickyChange(true, 'top');
				}
				if (
					targetInfo.bottom >= rootBoundsInfo.top &&
					targetInfo.bottom < rootBoundsInfo.bottom
				) {
					stickyChange(false, 'top');
				}
			},
			{threshold: [0], root: scrollContainer},
		);
		headerObserver.observe(sentinelTopRef.current as Element);

		return () => {
			footerObserver.disconnect();
			headerObserver.disconnect();
		};
	}, []);

	if (title == null) return null;

	return (
		<div
			className={clsx('sortable-set', classes.root, {
				[classes.isDragging]: sortable.isDragging && sortable.isSorting,
				[classes.stuck]: stuck,
			})}
			ref={disableSort ? undefined : sortable.setNodeRef}
			style={disableSort ? undefined : style}
		>
			<div
				className={clsx(classes.stickySentinel, classes.stickySentinel_top)}
				ref={sentinelTopRef}
			/>
			<div className={classes.title} ref={stickyTargetRef}>
				<IconButton
					className={classes.dragHandle}
					{...(disableSort ? {} : sortable.listeners)}
					{...(disableSort ? {} : sortable.attributes)}
					label=""
					icon={EqualsIcon}
					disabled={disableSort || sortable.isDragging}
				/>
				<div className={classes.titleText}>
					<Title primary={title} PrimaryTitleProps={{variant: 'h6'}} />
				</div>
			</div>
			<div
				className={clsx(classes.content, {
					[classes.searchable]: props.onSearch != null,
				})}
				ref={ref}
			>
				<AttributeSet {...props} />
			</div>
			<div
				className={clsx(classes.stickySentinel, classes.stickySentinel_bottom)}
				ref={sentinelBottomRef}
			/>
		</div>
	);
};

export const SortableAttributeSet = withStyles(styles)(
	SortableAttributeSetComponent as any,
);
export default SortableAttributeSet;
